import { Component, OnInit, OnDestroy, AfterViewInit, Renderer2, ViewChild } from "@angular/core";
import { IHeaderAngularComp } from "ag-grid-angular";
import { Store } from "@ngrx/store";
import { Tooltip } from "primeng/tooltip";
import { SortingCleared } from "src/app/store/actions";
import { focusOutOfGrid } from "src/app/common/utils/ag-grid-a11y";

@Component({
  standalone: false,
  selector: "esp-table-header",
  templateUrl: "./esp-table-header.component.html",
  styleUrls: ["./esp-table-header.component.scss"]
})
export class EspTableHeaderComponent implements OnInit, OnDestroy, AfterViewInit, IHeaderAngularComp {
  params: any;
  public currentSortOrder = "";
  colId: string;

  // WCAG 1.4.13 Content on Hover or Focus: the column-name tooltip (which
  // reveals the full, often-truncated header text) must also appear on keyboard
  // focus, not only mouse hover. AG Grid parks keyboard focus on the header cell
  // wrapper (params.eGridHeader), never on our inner <button>, so PrimeNG's own
  // focus listener on the button never fires. We bridge that by activating this
  // label tooltip when the header cell gains focus.
  @ViewChild(Tooltip) private labelTooltip?: Tooltip;
  private headerFocusUnlisten: (() => void) | null = null;
  private headerBlurUnlisten: (() => void) | null = null;
  private nonSortableFocusUnlisten: (() => void) | null = null;
  private nonSortableKeydownUnlisten: (() => void) | null = null;
  private sortableKeydownUnlisten: (() => void) | null = null;
  private docKeydownUnlisten: (() => void) | null = null;
  // Tracks the direction of the most recent Tab press so a focus that AG Grid
  // redirects onto a non-sortable header can be forwarded the correct way.
  private lastTabWasShift = false;
  private readonly onDocKeydown = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      this.lastTabWasShift = event.shiftKey;
    }
  };

  public static headers:Map<string,EspTableHeaderComponent> = new Map<string,EspTableHeaderComponent>();

  constructor(private renderer: Renderer2, private store: Store<any> ) {

  }

  ngOnInit() { }

  ngAfterViewInit() {
    const headerEl: HTMLElement = this.params?.eGridHeader;
    if (!headerEl) return;

    // Most non-sortable headers (for example the visually hidden Select header)
    // are not useful Tab stops and are skipped. A visible named header such as
    // Actions can explicitly opt into the sequence. It remains a plain
    // columnheader rather than becoming a misleading sort button.
    if (!this.params?.enableSorting) {
      if (this.params?.includeInHeaderTabOrder === true) {
        this.renderer.setAttribute(headerEl, "tabindex", "0");
        this.renderer.addClass(headerEl, "esp-static-header-tab-stop");
        this.nonSortableKeydownUnlisten = this.renderer.listen(
          headerEl,
          "keydown",
          (event: KeyboardEvent) => this.onStaticHeaderKeydown(event, headerEl)
        );
        return;
      }

      this.renderer.setAttribute(headerEl, "tabindex", "-1");
      this.docKeydownUnlisten = this.renderer.listen(
        "document",
        "keydown",
        (e: KeyboardEvent) => this.onDocKeydown(e)
      );
      this.nonSortableFocusUnlisten = this.renderer.listen(headerEl, "focus", () =>
        this.forwardNonSortableHeaderFocus(headerEl)
      );
      return;
    }

    // Sortable headers expose an inner <button.customHeaderLabel> as their
    // native Tab stop. When such a header is the LAST focusable header in the
    // row (for example the user reordered the Actions column out of the
    // trailing slot so a sortable column is now last), native Tab would leave
    // the header row and skip any managed body control that is deliberately
    // tabindex="-1" (such as the first-column Actions Delete button). Bridge to
    // the managed body sequence so the intended first body control is focused
    // instead of being skipped (ESP-21720 body counterpart).
    this.sortableKeydownUnlisten = this.renderer.listen(
      headerEl,
      "keydown",
      (event: KeyboardEvent) => this.onSortableHeaderKeydown(event, headerEl)
    );

    // Some grids intentionally keep header tooltips mouse-only. Do not bridge
    // wrapper focus to the PrimeNG tooltip for those grids; the template also
    // switches tooltipEvent to hover so focus on the inner button stays silent.
    if (!this.labelTooltip || this.params?.showTooltipOnFocus === false) return;
    // focus/blur do not bubble, so listen directly on the element AG Grid
    // focuses. Show the full-name tooltip on focus, dismiss it on blur.
    this.headerFocusUnlisten = this.renderer.listen(headerEl, "focus", () => {
      this.labelTooltip?.activate();
    });
    this.headerBlurUnlisten = this.renderer.listen(headerEl, "blur", () => {
      this.labelTooltip?.deactivate();
    });
  }

  /**
   * Move focus off a non-sortable (non-actionable) header onto the nearest
    * sortable header cell in the current Tab direction, so grid entry never
    * rests on an empty header. At the trailing boundary, give an interactive
    * grid a chance to move to its first body control before falling back to an
    * external control. At the leading boundary, reverse traversal leaves the
    * grid (e.g. returns to the Plan List search field).
   */
  private forwardNonSortableHeaderFocus(headerEl: HTMLElement): void {
    const headerRow = headerEl.closest(".ag-header-row");
    if (!headerRow) return;
    const buttons = Array.from(
      headerRow.querySelectorAll<HTMLElement>("button.customHeaderLabel")
    ).filter((btn) => btn.offsetParent !== null);
    if (!buttons.length) return;

    const forward = !this.lastTabWasShift;
    let dest: HTMLElement | null = null;
    for (const btn of buttons) {
      const pos = headerEl.compareDocumentPosition(btn);
      if (forward) {
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
          dest = btn; // first control after this header — closest forward stop
          break;
        }
      } else if (pos & Node.DOCUMENT_POSITION_PRECEDING) {
        dest = btn; // keep updating so we end on the closest control before it
      }
    }
    // AG Grid's native header navigation focuses .ag-header-cell wrappers.
    // Forwarding to the inner button here caused an inconsistent small rounded
    // ring on first entry, while returning with Shift+Tab produced the desired
    // full rectangular cell ring. Always focus the wrapper for parity.
    const destinationCell = dest?.closest<HTMLElement>(".ag-header-cell");
    if (destinationCell) {
      destinationCell.focus();
      return;
    }

    // A skipped trailing non-sortable header is a header boundary, not the end
    // of an interactive grid: continue into its first row control. The callback
    // is opt-in so read-only grids retain their existing exit behavior.
    // Returning false means there is no rendered body control.
    if (forward && typeof this.params?.focusFirstBodyControl === "function") {
      if (this.params.focusFirstBodyControl()) return;
    }

    // There is no actionable target on this side of the header row. Skip the
    // non-sortable cell and continue to the adjacent control outside the grid
    // (the Plan List search field on reverse traversal, or the next page
    // control when the grid has no rendered interactive body control).
    const gridHost = headerEl.closest<HTMLElement>("ag-grid-angular");
    focusOutOfGrid(gridHost, !forward);
  }

  /**
   * Bridge a sortable header that is the LAST focusable header in the row into
   * the managed grid body sequence on forward Tab, so a tabindex="-1" managed
   * body control is not skipped by native Tab. When other header stops still
   * follow, or the grid provides no body handoff, native Tab is left untouched.
   */
  private onSortableHeaderKeydown(event: KeyboardEvent, headerEl: HTMLElement): void {
    if (event.key !== "Tab" || event.shiftKey) return;
    if (typeof this.params?.focusFirstBodyControl !== "function") return;

    const headerRow = headerEl.closest(".ag-header-row");
    const followingHeaderStops = headerRow
      ? Array.from(
          headerRow.querySelectorAll<HTMLElement>(
            ".ag-header-cell:has(button.customHeaderLabel), .ag-header-cell.esp-static-header-tab-stop"
          )
        ).filter(
          (header) =>
            header.offsetParent !== null &&
            (headerEl.compareDocumentPosition(header) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
        )
      : [];
    // Not the last focusable header: let native Tab continue to the next header.
    if (followingHeaderStops.length > 0) return;

    if (this.params.focusFirstBodyControl() === true) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /** Handle the boundary keys for an explicitly focusable static header. */
  private onStaticHeaderKeydown(event: KeyboardEvent, headerEl: HTMLElement): void {
    if (event.key !== "Tab") return;

    let moved = false;
    if (event.shiftKey) {
      const headerRow = headerEl.closest(".ag-header-row");
      const sortableHeaders = headerRow
        ? Array.from(headerRow.querySelectorAll<HTMLElement>(".ag-header-cell:has(button.customHeaderLabel)"))
            .filter((header) => header.offsetParent !== null)
        : [];
      const previousHeaders = sortableHeaders.filter(
        (header) => header.compareDocumentPosition(headerEl) & Node.DOCUMENT_POSITION_FOLLOWING
      );
      const previous = previousHeaders[previousHeaders.length - 1];
      if (previous) {
        previous.focus();
        moved = true;
      }
    } else {
      // Forward Tab. Only hand off to the grid body when this static header is
      // the LAST focusable header in the row. If other column headers still
      // follow it (e.g. the user reordered the Actions column out of the
      // trailing slot), let native Tab continue to the next header instead of
      // jumping over the remaining headers straight into the body (ESP-21720).
      const headerRow = headerEl.closest(".ag-header-row");
      const followingHeaderStops = headerRow
        ? Array.from(
            headerRow.querySelectorAll<HTMLElement>(
              ".ag-header-cell:has(button.customHeaderLabel), .ag-header-cell.esp-static-header-tab-stop"
            )
          ).filter(
            (header) =>
              header.offsetParent !== null &&
              (headerEl.compareDocumentPosition(header) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
          )
        : [];
      if (followingHeaderStops.length > 0) {
        // Let the browser move focus to the next header naturally.
        return;
      }
      if (typeof this.params?.focusFirstBodyControl === "function") {
        moved = this.params.focusFirstBodyControl() === true;
      }
    }

    if (!moved) {
      const gridHost = headerEl.closest<HTMLElement>("ag-grid-angular");
      moved = focusOutOfGrid(gridHost, event.shiftKey);
    }

    if (moved) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  ngOnDestroy() {
    this.headerFocusUnlisten?.();
    this.headerBlurUnlisten?.();
    this.nonSortableFocusUnlisten?.();
    this.nonSortableKeydownUnlisten?.();
    this.sortableKeydownUnlisten?.();
    this.docKeydownUnlisten?.();
    this.headerFocusUnlisten = null;
    this.headerBlurUnlisten = null;
    this.nonSortableFocusUnlisten = null;
    this.nonSortableKeydownUnlisten = null;
    this.sortableKeydownUnlisten = null;
    this.docKeydownUnlisten = null;
  }

  onSortChanged() {
    if (this.params.column.isSortAscending()) {
      this.currentSortOrder = SortOrder.ASC;
    } else if (this.params.column.isSortDescending()) {
      this.currentSortOrder = SortOrder.DESC;
    } else {
      this.currentSortOrder = SortOrder.NONE;
    }
    this.updateAriaSort();
  }

  private updateAriaSort() {
    const headerEl: HTMLElement = this.params.eGridHeader;
    if (!headerEl) return;
    const ariaSort = this.currentSortOrder === SortOrder.ASC ? 'ascending' :
                     this.currentSortOrder === SortOrder.DESC ? 'descending' : 'none';
    this.renderer.setAttribute(headerEl, 'aria-sort', ariaSort);
  }

  onSortRequested(order, event) {
    if(this.params.context && !this.params.context.isLoading())
      this.params.setSort(order, event.shiftKey);
  }

  toggleSortOrder(event) {
    if (!this.params.enableSorting || (this.params.context && this.params.context.isLoading())) {
      return;
    }
    switch (this.currentSortOrder) {
      case SortOrder.ASC:
        this.currentSortOrder = SortOrder.DESC;
        break;
      case SortOrder.DESC:
        this.currentSortOrder = SortOrder.NONE;
        break;
      case SortOrder.NONE:
        this.currentSortOrder = SortOrder.ASC;
        break;
    }
    this.params.setSort(this.currentSortOrder, event.shiftKey);
    if(this.currentSortOrder == SortOrder.NONE)
      this.store.dispatch(new SortingCleared(""));
    else
      this.clearOtherSorting();


  }

  public static setSortOrder(colId:string, order:string){
    const headerComp: EspTableHeaderComponent = EspTableHeaderComponent.headers.get(colId);
    if (!headerComp) return;
    headerComp.currentSortOrder = order;
    headerComp.updateAriaSort();
  }

  clearOtherSorting() {
    EspTableHeaderComponent.headers.forEach((value: EspTableHeaderComponent, key: string) => {
      if (this.colId.indexOf(key) < 0) {
        const headerComp: EspTableHeaderComponent = value;
        headerComp.currentSortOrder = SortOrder.NONE;
        headerComp.updateAriaSort();
      }
    });

  }

  public static clearSortOrder() {
    EspTableHeaderComponent.headers.forEach((value: EspTableHeaderComponent) => {
      const headerComp: EspTableHeaderComponent = value;
      headerComp.currentSortOrder = SortOrder.NONE;
      headerComp.updateAriaSort();
    });
  }

  public static getSingleColumnSortModel(colId: string, sort: string, colIdArr: string[]) {
    const result = [];
    colIdArr.forEach((singleColId) => {
      if (singleColId === colId) result.push({ colId, sort });
      // null (not "") clears a column's sort: AG Grid v32+ applyColumnState
      // ignores sort values other than "asc" / "desc" / null.
      else result.push({ colId: singleColId, sort: null });
    });
    return result;
  }

  agInit(params) {
    EspTableHeaderComponent.headers.set(params.column.colDef.colId, this);
    this.params = params;
    this.colId = params.column.colDef.colId ? params.headerPrefix + params.column.colDef.colId : "";
    this.renderer.listen(this.params.column, "sortChanged", this.onSortChanged.bind(this));
    this.onSortChanged(); // initialise sort state + aria-sort
  }


  getSortOrderStatus(order: SortOrder){
    return (this.currentSortOrder == order)? "active": "inactive";
  }
  refresh(params: import("ag-grid-community").IHeaderParams): boolean {

    return false;
  }
}

enum SortOrder{
  ASC = "asc", DESC = "desc", NONE=""
}
